import { DatePicker, notification } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Button from '../../../../../../components/Button';
import { ButtonCancel } from '../../../../../../components/Button/styles';
import { formatarData } from '../../../../../../helpers/data';
import apiShipper from '../../../../../../services/apiShipper';
import { useAgendamentoAlteracaoContext } from '../../../../Contextos/AgendamentoAlteracaoContexto';
import StatusAlteracaoEnum from '../../../../Enum/StatusAlteracaoEnum';
import StatusLoteEnum from '../../../../Enum/StatusLoteEnum';
import TipoAlteracaoEnum from '../../../../Enum/TipoAlteracaoEnum';
import {
  AgendamentoContainer,
  AgendamentoInfo,
  CustomModal,
  FieldContainer,
} from '../styles';

const SuspenderModal = ({
  visible,
  setVisible,
  agendamento,
  setAgendamento,
  actionPayload,
}) => {
  const [confirmLoading, setConfirmLoading] = useState({
    agora: false,
    agendar: false,
    cancelar: false,
  });
  const [justificativa, setJustificativa] = useState('');
  const [dataAgendamento, setDataAgendamento] = useState('');
  const history = useHistory();
  const { removeAgendamentoAlteracao } = useAgendamentoAlteracaoContext();

  const resetLoading = () => {
    setConfirmLoading({
      agora: false,
      agendar: false,
      cancelar: false,
    });
  };

  const handleAlterarAgora = async () => {
    setConfirmLoading({ agora: true, agendar: false, cancelar: false });

    try {
      const payload = {
        data_alteracao: moment().format(),
        motivo_suspensao: justificativa,
        status_lote: StatusLoteEnum.SUSPENSO,
        ...actionPayload,
      };

      await apiShipper.post('alteracao-demanda', payload);

      notification.success({
        message: 'Sucesso',
        description: 'Lotes suspensos com sucesso',
      });
      history.push({
        pathname: `/detalhesDemanda/${actionPayload.id_demanda}`,
      });
    } catch {
      notification.error({
        message: 'Ocorreu um erro inesperado ao suspender os lotes',
        description: 'Se o problema persistir, entre em contato com o suporte',
      });
    } finally {
      resetLoading();
      setVisible();
    }
  };

  const handleAgendar = async () => {
    setConfirmLoading({ agora: false, agendar: true, cancelar: false });

    try {
      const payload = {
        data_alteracao: moment(dataAgendamento)
          .utc(true)
          .format(),
        status_lote: StatusLoteEnum.SUSPENSO,
        motivo_suspensao: justificativa,
        tipo_alteracao: TipoAlteracaoEnum.SUSPENDER,
        ...actionPayload,
      };

      await apiShipper.post('alteracao-demanda', payload);

      notification.success({
        message: 'Sucesso!',
        description: 'Agendamento de suspensão realizado com sucesso.',
      });

      setAgendamento({ data: dataAgendamento, justificativa });
      history.push({
        pathname: `/detalhesDemanda/${actionPayload.id_demanda}`,
      });
    } catch {
      notification.error({
        message: 'Ocorreu um erro inesperado ao agendar a suspensão dos lotes',
        description: 'Se o problema persistir, entre em contato com o suporte',
      });
    } finally {
      resetLoading();
      setVisible();
    }
  };

  const handleCancelAgendamento = async () => {
    setConfirmLoading({ agora: false, agendar: false, cancelar: true });

    const status = StatusAlteracaoEnum.status.CANCELADO;

    try {
      await apiShipper.put('alteracao-demanda', {
        id_demanda: actionPayload.id_demanda,
        status,
        tipo_alteracao: TipoAlteracaoEnum.SUSPENDER,
      });

      notification.success({
        message: 'Sucesso!',
        description: 'Agendamento de suspensão cancelado com sucesso.',
      });

      removeAgendamentoAlteracao(agendamento);
      setAgendamento({ data: null, justificativa: null });
      setDataAgendamento(null);
    } catch (error) {
      notification.error({
        message: 'Ocorreu um erro inesperado ao cancelar o agendamento',
        description: 'Se o problema persistir, entre em contato com o suporte',
      });
    } finally {
      resetLoading();
      setVisible();
    }
  };

  const dataFormatada = dataAgendamento || formatarData(agendamento.data);

  useEffect(() => {
    if (!visible) {
      setDataAgendamento(null);
      setJustificativa(null);
    }
  }, [visible]);

  return (
    <CustomModal
      closable={false}
      title="Suspender todos os lotes"
      visible={visible}
      footer={[
        <Button key="fechar" type="secondary" onClick={() => setVisible()}>
          Fechar
        </Button>,
        <Button
          key="alterar"
          type="secondary"
          loading={confirmLoading.agora}
          onClick={handleAlterarAgora}
          disabled={!justificativa || dataAgendamento}
        >
          Suspender agora
        </Button>,
        <Button
          key="agendar"
          type="primary"
          loading={confirmLoading.agendar}
          onClick={handleAgendar}
          disabled={!dataAgendamento || !justificativa}
        >
          Agendar
        </Button>,
      ]}
    >
      <FieldContainer>
        <p>
          Você poderá retomar o carregamento novamente. Notificaremos a
          alteração para as transportadoras.
        </p>
      </FieldContainer>
      <FieldContainer>
        <TextArea
          data-testid="text-justificativa"
          required
          rows={4}
          placeholder="Justificativa"
          value={justificativa}
          onChange={e => setJustificativa(e.target.value)}
        />
      </FieldContainer>
      <FieldContainer>
        <p>Agendar alteração</p>
        <AgendamentoContainer>
          <DatePicker
            data-testid="data-agendamento"
            value={dataAgendamento}
            name="dataAgendamento"
            format="DD/MM/YYYY"
            disabled={agendamento.data}
            disabledDate={current => current.isBefore(moment())}
            style={{ width: '60%' }}
            onChange={data => setDataAgendamento(data)}
          />
          <div style={{ width: '10px' }} />
          {agendamento.data && (
            <ButtonCancel
              loading={confirmLoading.cancelar}
              onClick={handleCancelAgendamento}
            >
              Cancelar
            </ButtonCancel>
          )}
        </AgendamentoContainer>
      </FieldContainer>
      {agendamento.data && (
        <AgendamentoInfo>
          <p>{`Suspensão agendada para ${dataFormatada}`}</p>
          <p>
            <b>{`Justificativa: ${agendamento.justificativa}`}</b>
          </p>
        </AgendamentoInfo>
      )}
    </CustomModal>
  );
};

SuspenderModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  agendamento: PropTypes.shape({
    data: PropTypes.string,
    justificativa: PropTypes.string,
  }),
  setAgendamento: PropTypes.func.isRequired,
  actionPayload: PropTypes.shape({
    id_demanda: PropTypes.number,
    cliente_razao_social: PropTypes.string,
    origem: PropTypes.string,
    destino: PropTypes.string,
    email_filiais: PropTypes.arrayOf(PropTypes.string),
  }),
};

SuspenderModal.defaultProps = {
  agendamento: null,
  actionPayload: null,
};

export default SuspenderModal;
